import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flag, X, Send, AlertTriangle } from "lucide-react";
import { reportMessage, REPORT_REASON_CODES } from "@/lib/modulD";
import { InfoCard } from "@/components/toggles";

/**
 * Message-Report-Dialog (Sub-Batch 4c.5, REQ-FE-D-REPORT-002)
 *
 * Wiederverwendbare Modal-Component für das Melden von Nachrichten.
 *
 * Props:
 *  - open: bool – Dialog sichtbar
 *  - onClose: () => void
 *  - messageKind: "CONV" | "DM" | "MP_INQUIRY" | "PRESALE_MSG"
 *  - messageId: string
 *  - messagePreview: string – kurze Anzeige der gemeldeten Nachricht (max ~100 chars)
 *  - onReported: () => void – wird nach Erfolg aufgerufen (Parent merkt sich gemeldete IDs)
 */
export default function MessageReportDialog({
  open, onClose, messageKind, messageId, messagePreview, onReported,
}) {
  const { t } = useTranslation();
  const [reasonCode, setReasonCode] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!reasonCode || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await reportMessage(messageKind, messageId, reasonCode, comment.trim());
      setDone(true);
      onReported?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail?.code;
      if (code === "ALREADY_REPORTED") {
        setError(t("message_report.already_reported"));
      } else if (code === "CANNOT_REPORT_OWN_MESSAGE") {
        setError(t("message_report.cannot_report_own"));
      } else if (code === "NOT_PARTICIPANT") {
        setError(t("message_report.not_participant"));
      } else if (code === "MESSAGE_NOT_FOUND") {
        setError(t("message_report.not_available"));
      } else if (code === "COMMENT_TOO_LONG") {
        setError(t("message_report.comment_too_long"));
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(t("message_report.submit_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeDialog = () => {
    setReasonCode("");
    setComment("");
    setError(null);
    setDone(false);
    onClose();
  };

  return (
    <div
      data-testid="report-dialog-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={closeDialog}
    >
      <div
        data-testid="report-dialog"
        className="w-full max-w-md bg-white border border-[#E5E5E5] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Flag size={16} className="text-red-600" />
            <h3 className="text-sm font-bold text-[#0A0A0A]">{t("message_report.title")}</h3>
          </div>
          <button
            data-testid="report-close"
            onClick={closeDialog}
            className="text-[#999] hover:text-[#0A0A0A]"
            aria-label={t("message_report.close_aria")}
          >
            <X size={18} />
          </button>
        </div>

        {done ? (
          /* Success state */
          <div className="px-4 py-6 text-center">
            <InfoCard variant="success" className="mx-auto w-12 mb-3">
              <Flag size={20} className="text-green-600" />
            </InfoCard>
            <p className="text-sm font-semibold text-[#0A0A0A]">{t("message_report.thanks")}</p>
            <p className="text-xs text-[#666] mt-1 leading-relaxed">
              Wir prüfen die Nachricht und melden uns bei dir, sobald die Prüfung abgeschlossen ist.
            </p>
            <button
              data-testid="report-done-close"
              onClick={closeDialog}
              className="mt-4 h-9 px-4 bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#222]"
            >
              Schließen
            </button>
          </div>
        ) : (
          /* Form state */
          <div className="px-4 py-3 space-y-3">
            {messagePreview && (
              <div className="px-3 py-2 bg-[#F5F5F5] border-l-2 border-[#999]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider mb-0.5">{t("message_report.reported_msg")}</p>
                <p className="text-xs text-[#0A0A0A] line-clamp-3">{messagePreview}</p>
              </div>
            )}

            <div>
              <label htmlFor="report-reason" className="block text-[11px] font-semibold text-[#0A0A0A] mb-1">
                Grund <span className="text-red-600">*</span>
              </label>
              <select
                id="report-reason"
                data-testid="report-reason-select"
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                disabled={submitting}
                className="w-full rounded-full h-9 px-2 border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
              >
                <option value="">{t("message_report.select_placeholder")}</option>
                {REPORT_REASON_CODES.map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="report-comment" className="block text-[11px] font-semibold text-[#0A0A0A] mb-1">
                Zusätzliche Info <span className="text-[#999]">(optional, max. 500 Zeichen)</span>
              </label>
              <textarea
                id="report-comment"
                data-testid="report-comment"
                rows={3}
                maxLength={500}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitting}
                placeholder={t("message_report.comment_placeholder")}
                className="w-full px-2 py-1.5 border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] resize-none"
              />
              <p className="text-[10px] text-[#999] mt-0.5 text-right">{comment.length}/500</p>
            </div>

            {error && (
              <InfoCard variant="danger">
                <AlertTriangle size={14} className="text-red-600 mt-0.5 shrink-0" />
                <p className="text-[11px] text-red-700 leading-relaxed">{error}</p>
              </InfoCard>
            )}

            <div className="flex gap-2 pt-1">
              <button
                data-testid="report-cancel"
                onClick={closeDialog}
                disabled={submitting}
                className="flex-1 h-9 border border-[#E5E5E5] text-[#0A0A0A] text-xs font-semibold hover:bg-[#F5F5F5] disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                data-testid="report-submit"
                onClick={handleSubmit}
                disabled={!reasonCode || submitting}
                className="flex-1 h-9 bg-red-600 text-white text-xs font-semibold hover:bg-red-500 disabled:opacity-40 flex items-center justify-center gap-1"
              >
                <Send size={12} />
                {submitting ? "Wird gemeldet…" : "Meldung absenden"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
